import { ApiError, type UploadTicket } from "./api";
import type {
  AlertAcknowledged,
  PortalAlerts,
  PortalHome,
  PortalMessage,
  PortalMessages,
  PortalPickups,
  PortalRebates,
  PortalRequests,
  PortalSettings,
  RequestSent,
  requestBody,
} from "./portalView";
import { SERVER_PROBLEM } from "./save";

type RequestBody = ReturnType<typeof requestBody>;

export const PORTAL_NAME = "Xflow Customer";
export const INVITE_DAYS = 7; // INVITE_DAYS in platform/api/app/portal.py
export const PORTAL_OFF = "The customer portal is not open yet. Try again later.";
export const PORTAL_NOT_READY = "The customer portal is not ready yet. Try again later.";
export const INVITE_NOT_VALID =
  "This invite is not valid. It was used, it is more than 7 days old, or the link is not complete. Ask your collector for a new invite.";
export const LINK_NOT_VALID = "This sign-in link is not valid. It was used, or it is more than 15 minutes old. Enter your email address to get a new link.";
export const NO_ACCESS = "This account has no Locations in the customer portal. Ask your collector for an invite.";
const NO_CONNECTION = "Cannot reach Xflow. Check your connection, then try again.";

export interface PortalBootstrap {
  name: string;
  sign_in: string[];
  identity: { api_key: string; auth_domain: string; tenant_id: string };
}

export interface InviteView {
  collector: string;
  location: string;
  /** The address of the account; an older API leaves it out. */
  address?: string;
  email: string;
  expires_at: string;
}

/** The newest invite of an account that still works (masked email). */
export interface OpenInvite {
  email: string;
  sent_at: string;
  expires_at: string;
  sent_by: string | null;
}

/** The heading of the portal pages before the sign-in, and the path that a sign-out opens. */
export const PORTAL_HEADING = "Customer portal";
export const SIGN_IN_PATH = "/portal";

/** The browser tab title before the sign-in: "Sign in · Customer portal". */
export function signInTitle(step: string): string {
  if (step === "invite") return `Your invite · ${PORTAL_HEADING}`;
  if (step === "email") return `Sign in · ${PORTAL_HEADING}`;
  return PORTAL_HEADING;
}

/** One name for the portal on the collector pages. */
export const PORTAL_TITLE = `Customer portal (${PORTAL_NAME})`;
export const EMAIL_MAX = 254; // EMAIL_MAX in platform/api/app/portal_routes.py
export const EMAIL_TOO_LONG = "The email address is too long.";
export const NO_EMAIL = "Enter the email address of the restaurant manager.";

export interface PortalLocation {
  id: string;
  name: string;
  address: string;
  city: string;
  organization_id: string;
  collector: string | null;
  can: string[];
  /** The time zone of the Location; an older API leaves it out. */
  timezone?: string | null;
}

export interface PortalMe {
  email: string | null;
  organizations: { id: string; name: string }[];
  roles: string[];
  locations: PortalLocation[];
}

export interface InviteResult {
  status: string;
  email: string;
  mailed: boolean;
  expires_at: string;
  /** An older API leaves it out. */
  invite?: OpenInvite | null;
}

async function call<T>(path: string, init: RequestInit = {}, headers: Record<string, string> = {}): Promise<T> {
  const all = init.body ? { ...headers, "Content-Type": "application/json" } : headers;
  const response = await fetch(`/api${path}`, { ...init, headers: all });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new ApiError(response.status, typeof body.detail === "string" ? body.detail : undefined);
  return body as T;
}

const bearer = (token: string) => ({ Authorization: `Bearer ${token}` });
const post = <T>(path: string, body: object, headers: Record<string, string> = {}) =>
  call<T>(path, { method: "POST", body: JSON.stringify(body) }, headers);

export const portalBootstrap = () => call<PortalBootstrap>("/portal/bootstrap");
export const peekInvite = (code: string) => call<InviteView>("/portal/accept", {}, { "X-Invite": code });
export interface AcceptResult {
  location_id: string | null;
  custom_token: string | null;
  /** An older API leaves it out. True: the email has a portal account, so sign in first. */
  sign_in_first?: boolean;
}

export const acceptInvite = (code: string, token?: string | null) =>
  post<AcceptResult>("/portal/accept", { code }, token ? bearer(token) : {});
export const portalMe = (token: string) => call<PortalMe>("/portal/me", {}, bearer(token));
export const portalHome = (token: string) => call<PortalHome>("/portal/home", {}, bearer(token));
export const portalLocations = (token: string) => call<PortalHome>("/portal/locations", {}, bearer(token));
export const portalSettings = (token: string) => call<PortalSettings>("/portal/settings", {}, bearer(token));
export const setTheftMail = (token: string, on: boolean) =>
  call<{ theft_mail: boolean }>("/portal/settings/theft-mail", { method: "PUT", body: JSON.stringify({ on }) }, bearer(token));
export function portalPickups(token: string, days: number, location?: string | null) {
  const query = new URLSearchParams({ days: String(days) });
  if (location) query.set("location", location);
  return call<PortalPickups>(`/portal/pickups?${query}`, {}, bearer(token));
}

export function portalRequests(token: string, location?: string | null) {
  const query = new URLSearchParams();
  if (location) query.set("location", location);
  return call<PortalRequests>(`/portal/requests${location ? `?${query}` : ""}`, {}, bearer(token));
}
export const requestPickup = (token: string, body: RequestBody) => post<RequestSent>("/portal/requests", body, bearer(token));

export interface PortalPhotoEntry {
  location_id: string;
  id: string;
  content_type: string;
  size_bytes: number;
  sha256: string;
  /** An older API ignores it; a newer one refuses an open request of the kind before the upload. */
  kind?: string;
}
export const startPortalPhoto = (token: string, entry: PortalPhotoEntry) => post<UploadTicket>("/portal/photos", entry, bearer(token));
export const portalPhotoStored = (token: string, id: string, locationId: string) =>
  post<{ photo: { id: string; stored: boolean } }>(`/portal/photos/${encodeURIComponent(id)}/stored`, { location_id: locationId }, bearer(token));
export const portalPhotoUrl = (token: string, id: string) => call<{ url: string }>(`/portal/photos/${encodeURIComponent(id)}`, {}, bearer(token));

export function portalRebates(token: string, days: number, location?: string | null) {
  const query = new URLSearchParams({ days: String(days) });
  if (location) query.set("location", location);
  return call<PortalRebates>(`/portal/rebates?${query}`, {}, bearer(token));
}

export const portalAlerts = (token: string) => call<PortalAlerts>("/portal/alerts", {}, bearer(token));
export const acknowledgeAlert = (token: string, id: string) =>
  post<AlertAcknowledged>(`/portal/alerts/${encodeURIComponent(id)}/acknowledge`, {}, bearer(token));

/** SO-1462: an older API has no message route; then the contacts come from Home. */
export async function portalMessages(token: string): Promise<PortalMessages> {
  try {
    return await call<PortalMessages>("/portal/messages", {}, bearer(token));
  } catch (error) {
    if (!(error instanceof ApiError && error.status === 404 && error.detail === "Not Found")) throw error;
    const home = await portalHome(token);
    return { days: 0, more: false, messages: [], can_send: [], contacts: home.contacts ?? [], text_max: 0, lagging: true };
  }
}
export const sendMessage = (token: string, body: { location_id: string; text: string }) =>
  post<{ message: PortalMessage }>("/portal/messages", body, bearer(token));

const PENDING = "portal-invite";
export const SIGN_IN_FIRST =
  "This email has a portal account already. Sign in with your email address first. Then accept the invite, in this browser.";

const KEEP_MS = 86_400_000;

/** Keep the invite code while the user signs in: the sign-in link can open in a new tab. */
export function keepInvite(code: string | null, now: number = Date.now()) {
  try {
    if (code) localStorage.setItem(PENDING, JSON.stringify({ code, at: now }));
    else localStorage.removeItem(PENDING);
  } catch {
    console.warn("portal: local storage is off; the invite is not kept");
  }
}

/** The kept invite code, or null. A code older than one day is not used. */
export function keptInvite(now: number = Date.now()): string | null {
  try {
    const kept = JSON.parse(localStorage.getItem(PENDING) ?? "null") as { code?: unknown; at?: unknown } | null;
    if (!kept || typeof kept.code !== "string" || typeof kept.at !== "number") return null;
    return now - kept.at <= KEEP_MS ? kept.code : null;
  } catch {
    return null;
  }
}
export const portalLink = (email: string) => post<{ status: string }>("/portal/signin/link", { email });
export const portalRedeem = (code: string) => post<{ custom_token: string }>("/portal/signin/redeem", { code });
export const sendInvite = (token: string, accountId: string, email: string) =>
  post<InviteResult>(`/accounts/${encodeURIComponent(accountId)}/portal-invite`, { email }, bearer(token));
export const cancelInvite = (token: string, accountId: string) =>
  call<{ cancelled: number }>(`/accounts/${encodeURIComponent(accountId)}/portal-invite`, { method: "DELETE" }, bearer(token));

/** The problem of the invite email field, or null. A pasted text over the limit is not cut. */
export function inviteEmailProblem(email: string): string | null {
  if (email.trim().length > EMAIL_MAX) return EMAIL_TOO_LONG;
  return validEmail(email) ? null : NO_EMAIL;
}

/** An email as the API shows it to a collector: the first letter and the domain. */
export function maskEmail(email: string): string {
  const [name, domain] = email.trim().toLowerCase().split("@");
  return domain ? `${name.slice(0, 1)}***@${domain}` : "-";
}

/** The open invite after a send. An older API sends no invite: then it is made here. */
export function sentInvite(result: InviteResult, now: Date = new Date()): OpenInvite {
  return result.invite ?? { email: maskEmail(result.email), sent_at: now.toISOString(), expires_at: result.expires_at, sent_by: null };
}

/** "Invite sent to j***@example.com on Oct 22 by Sam. It works until Oct 29." */
export function openInviteText(invite: OpenInvite): string {
  const by = invite.sent_by ? ` by ${invite.sent_by.includes("@") ? invite.sent_by.split("@")[0] : invite.sent_by}` : "";
  return `Invite sent to ${invite.email} on ${day(invite.sent_at)}${by}. It works until ${day(invite.expires_at)}.`;
}

export const INVITE_CANCELLED = "The invite is cancelled. Its link does not work now.";

/** The invite code or the sign-in code of the portal link in the hash, or null. */
export function portalCodes(hash: string = location.hash): { invite: string | null; link: string | null } {
  const params = new URLSearchParams(hash.replace(/^#/, ""));
  return { invite: params.get("invite") || null, link: params.get("link") || null };
}

export type Start = "invite" | "link" | "signed-in" | "email";

/** The first step of the portal page: an invite, a sign-in link, a kept session, or the email form. */
export function portalStart(hash: string, signedIn: boolean): Start {
  const codes = portalCodes(hash);
  if (codes.invite) return "invite";
  if (codes.link) return "link";
  return signedIn ? "signed-in" : "email";
}

const status = (error: unknown) => (error instanceof ApiError ? error.status : null);

/** The words for a failed invite check or accept: 404 is each bad, used or old invite. */
export function inviteError(error: unknown): string {
  const answered = status(error);
  if (answered === 404) return INVITE_NOT_VALID;
  if (answered !== null) return SERVER_PROBLEM;
  return NO_CONNECTION;
}

export function linkError(error: unknown): string {
  const answered = status(error);
  if (answered === 400 || answered === 422) return LINK_NOT_VALID;
  if (answered === 404) return PORTAL_OFF;
  if (answered !== null) return SERVER_PROBLEM;
  return NO_CONNECTION;
}

export function meError(error: unknown): string {
  const answered = status(error);
  if (answered === 404) return NO_ACCESS;
  if (answered !== null) return SERVER_PROBLEM;
  return NO_CONNECTION;
}

export function validEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

const day = (iso: string) => new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });

export function inviteSentText(result: InviteResult): string {
  if (!result.mailed) {
    return `The invite for ${result.email} is saved, but no email went out. Send the invite again later. The email works for ${INVITE_DAYS} days.`;
  }
  return `Sent: an invite to ${result.email}. It works one time, until ${day(result.expires_at)}.`;
}

/** The words for a refused invite. A 404 with no detail is an older API with no portal route. */
export function inviteFailText(error: unknown): string {
  const answered = status(error);
  const detail = error instanceof ApiError ? error.detail : undefined;
  if (answered === 401) return "Your session ended. Sign in again.";
  if (answered === 422) return "Enter the email address of the restaurant manager.";
  if (answered === 404 && (!detail || detail === "not found")) return PORTAL_NOT_READY;
  if (answered !== null && detail) return detail;
  if (answered !== null) return SERVER_PROBLEM;
  return NO_CONNECTION;
}

export function linkedText(since: string): string {
  return `In the customer portal since ${day(`${since}T12:00:00`)}.`;
}

export function inviteViewText(view: InviteView): string {
  const place = view.address ? `${view.location} (${view.address})` : view.location;
  return `${view.collector} invites ${view.email} to see the service of ${place} in ${PORTAL_NAME}. This invite works until ${day(view.expires_at)}.`;
}

/** "You are in" and the Locations of the portal user, each with its collector. */
export function youAreInText(me: PortalMe): string {
  const names = me.locations.map((place) => (place.collector ? `${place.name} (${place.collector})` : place.name));
  if (!names.length) return NO_ACCESS;
  return `You are in. ${PORTAL_NAME} shows ${names.join(", ")}.`;
}
